import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { Modal, TextInput } from "risa-oasis-ui_v2";
import { toAgenticAuthOnFilePayerKey } from "../../../pages/configurations/pages/agenticAuthOnFileConfiguration/utils/agenticAuthOnFileTable";
import { AgenticAuthOnFileConfigService } from "../../../../shared/api/services/agenticAuthOnFileConfigService";
import { ModalId } from "../../../../shared/enums/modalId";

interface AgenticAuthOnFileAddPayerModalProps {
  organizationId: string;
  existingPayerKeys: string[];
  updatedBy?: string;
  onClose: () => void;
  onAdded: () => void;
}

export default function AgenticAuthOnFileAddPayerModal({
  organizationId,
  existingPayerKeys,
  updatedBy,
  onClose,
  onAdded,
}: AgenticAuthOnFileAddPayerModalProps) {
  const [payerName, setPayerName] = useState("");
  const [live, setLive] = useState(false);
  const [agentMode, setAgentMode] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = payerName.trim();
    if (!trimmed) {
      toast.error("Please enter a payer name.");
      return;
    }
    const key = toAgenticAuthOnFilePayerKey(trimmed);
    if (!key) {
      toast.error("Please enter a payer name.");
      return;
    }
    const duplicate = existingPayerKeys.some(
      (id) => id.toLowerCase() === key.toLowerCase(),
    );
    if (duplicate) {
      toast.error("A payer with this name already exists.");
      return;
    }

    setSaving(true);
    try {
      await AgenticAuthOnFileConfigService.addPayer(
        organizationId,
        trimmed,
        { enabled: live, agent_mode: agentMode },
        remarks.trim(),
        updatedBy,
      );
      toast.success("Payer added.");
      onAdded();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to add payer.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      dialogId={ModalId.AGENTIC_AUTH_ON_FILE_ADD_PAYER_MODAL}
      title="Add payer"
      onCancel={onClose}
      onClose={onClose}
      onSave={handleSave}
      cancelText="Cancel"
      saveButtonText={saving ? "Adding..." : "Add payer"}
      disableSave={saving || !payerName.trim()}
    >
      <div className="flex flex-col gap-4">
        <TextInput
          id="agentic-auth-add-payer-name"
          label="Payer name"
          placeholder="Enter payer name"
          onChange={(e) => setPayerName(e.value)}
        />
        <div className="grid grid-cols-2 gap-4">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={live}
              onChange={(e) => setLive(e.target.checked)}
              className="h-4 w-4 shrink-0 cursor-pointer accent-black"
            />
            <span className="text-small font-medium text-primaryGray-1">
              Live
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={agentMode}
              onChange={(e) => setAgentMode(e.target.checked)}
              className="h-4 w-4 shrink-0 cursor-pointer accent-black"
            />
            <span className="text-small font-medium text-primaryGray-1">
              Agent mode
            </span>
          </label>
        </div>
        <div className="w-full">
          <div className="mb-2 text-small font-normal text-primaryGray-2">
            Remarks
          </div>
          <textarea
            className="h-20 w-full rounded-md border border-gray-300 p-2 text-tiny font-normal"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter remarks for this payer"
          />
        </div>
      </div>
    </Modal>
  );
}
